import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { usePageMeta } from "../../components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// V2 §6-§8 — Service Packages catalogue with pill/chip filter groups.
// OR within a filter group; AND across groups.  Backend list-values
// filter now supports multi-select per group (see `catalogue_list`).

const TYPE_OPTS = [
    { v: "Standard", l: "Standard" },
    { v: "Custom Global", l: "Custom" },
];
const STATUS_OPTS = [
    { v: "Active", l: "Active" },
    { v: "Retired", l: "Inactive" },
];
const LIFECYCLE_OPTS = [
    { v: "Pre-WS6", l: "Pre-WS6" },
    { v: "WS6", l: "WS6" },
    { v: "Any Workstage", l: "Any Workstage" },
    { v: "Post Free Design", l: "Post Free Design" },
];
const BASIS_OPTS = [
    { v: "LRS Paid Services Appointment", l: "LRS Paid Services Appointment" },
    { v: "External / Standard-Form Contract", l: "External / Standard-Form Contract" },
    { v: "Bespoke External Contract", l: "Bespoke External Contract" },
    { v: "Other Approved Instrument", l: "Other Approved Instrument" },
];
const PRICING_OPTS = [
    { v: "Fixed Fee", l: "Fixed Fee" },
    { v: "Time Charge", l: "Time Charge" },
    { v: "Unit-Based", l: "Unit-Based" },
    { v: "Monthly Retainer", l: "Monthly Retainer" },
    { v: "Lump Sum / Contract Sum", l: "Lump Sum / Contract Sum" },
    { v: "Percentage", l: "Percentage" },
    { v: "Cost Plus", l: "Cost Plus" },
    { v: "Custom", l: "Custom" },
];

function PillGroup({ testid, values, setValues, options }) {
    const toggle = (v) => {
        if (values.includes(v)) setValues(values.filter(x => x !== v));
        else setValues([...values, v]);
    };
    return (
        <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
            {options.map(o => {
                const on = values.includes(o.v);
                return (
                    <button key={o.v} type="button"
                            onClick={() => toggle(o.v)}
                            data-testid={`${testid}-opt-${o.v}`}
                            aria-pressed={on}
                            className={`px-3 py-1.5 rounded-full text-[12.5px] border transition select-none ${on
                                ? "bg-charcoal text-white border-charcoal shadow-sm"
                                : "bg-white text-charcoal border-[#DDD8D0] hover:border-charcoal/60"}`}>
                        {o.l}
                    </button>
                );
            })}
        </div>
    );
}

export default function ServicePackagesShell() {
    usePageMeta({ title: "Service Packages | Studio G4 Admin" });
    const [items, setItems] = useState(null);
    const [err, setErr] = useState("");
    const [type, setType] = useState([]);
    const [status, setStatus] = useState([]);
    const [lifecycle, setLifecycle] = useState([]);
    const [basis, setBasis] = useState([]);
    const [pricing, setPricing] = useState([]);
    const [q, setQ] = useState("");
    const [duplicating, setDuplicating] = useState(null);

    const params = useMemo(() => {
        const p = new URLSearchParams();
        for (const v of type) p.append("template_type", v);
        for (const v of status) p.append("status", v);
        for (const v of lifecycle) p.append("lifecycle_context", v);
        for (const v of basis) p.append("contract_basis", v);
        for (const v of pricing) p.append("pricing_model", v);
        if (q) p.set("q", q);
        return p.toString();
    }, [type, status, lifecycle, basis, pricing, q]);

    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/admin/service-packages/catalogue?${params}`,
                                       { withCredentials: true });
            setItems(r.data.items);
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
    }, [params]);
    useEffect(() => { refresh(); }, [refresh]);

    const clearAll = () => {
        setType([]); setStatus([]); setLifecycle([]); setBasis([]); setPricing([]); setQ("");
    };

    const duplicate = async (row) => {
        const name = window.prompt(
            `Duplicate template "${row.package_name}" as a new Custom Global template.\nEnter a name for the new template:`,
            `${row.package_name} (Custom)`);
        if (!name) return;
        setDuplicating(row.package_code);
        try {
            const opId = "dup-" + Date.now() + "-" + row.package_code;
            await axios.post(`${API}/admin/service-packages/catalogue/duplicate`,
                              { source_package_code: row.package_code,
                                new_name: name, duplicate_op_id: opId },
                              { withCredentials: true });
            await refresh();
        } catch (e) { alert(e?.response?.data?.error || e.message); }
        finally { setDuplicating(null); }
    };

    if (err) return <div data-testid="cat-error" className="text-red-700 text-[13px]">{err}</div>;

    return (
        <div className="flex flex-col gap-6" data-testid="service-packages-shell">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="font-manrope text-3xl font-semibold text-charcoal mb-1"
                        data-testid="service-packages-title">Service Packages</h1>
                    <p className="text-charcoal/60 text-[15px]">
                        Global catalogue of Standard and Custom Service Package templates.
                    </p>
                </div>
                <Link to="/admin/service-packages/new"
                      className="bg-[#A8543F] hover:bg-[#8f3e2c] text-white text-[13px] px-3 py-2 rounded font-manrope font-semibold"
                      data-testid="cat-new-template-btn">
                    + Create template
                </Link>
            </div>

            {/* V2 §6-§8 — pill filter groups */}
            <div className="bg-white border border-[#DDD8D0] rounded-lg p-4"
                 data-testid="cat-filters">
                <div className="flex items-center justify-between mb-2">
                    <b className="font-manrope text-[13px]">Filters</b>
                    <div className="flex items-center gap-2">
                        <input value={q}
                               onChange={(e) => setQ(e.target.value)}
                               placeholder="Search package name or code"
                               className="border border-[#DDD8D0] rounded px-3 py-1.5 text-[13px] w-[260px]"
                               data-testid="cat-filter-search" />
                        <button onClick={clearAll}
                                className="border border-[#DDD8D0] rounded px-3 py-1.5 text-[12.5px] font-manrope font-semibold hover:bg-[#faf9f7]"
                                data-testid="cat-filter-clear">
                            Clear filters
                        </button>
                    </div>
                </div>
                {[
                    { label: "Type", testid: "cat-filter-type",       values: type,       set: setType,      options: TYPE_OPTS },
                    { label: "Status", testid: "cat-filter-status",   values: status,     set: setStatus,    options: STATUS_OPTS },
                    { label: "Lifecycle", testid: "cat-filter-lifecycle", values: lifecycle, set: setLifecycle, options: LIFECYCLE_OPTS },
                    { label: "Contract basis", testid: "cat-filter-basis", values: basis,  set: setBasis,     options: BASIS_OPTS },
                    { label: "Pricing", testid: "cat-filter-pricing", values: pricing,    set: setPricing,   options: PRICING_OPTS },
                ].map(g => (
                    <div key={g.label} className="grid grid-cols-[140px_1fr] gap-4 py-2 border-t border-[#eee] first:border-t-0">
                        <div className="text-[11px] uppercase tracking-wider text-charcoal/60 font-manrope font-semibold pt-1.5">{g.label}</div>
                        <PillGroup testid={g.testid} values={g.values} setValues={g.set} options={g.options} />
                    </div>
                ))}
            </div>
            <p className="text-[11.5px] text-charcoal/60 font-inter" data-testid="cat-filter-legend">
                Multiple selections use OR within a filter group and AND across filter groups.
            </p>

            <div className="bg-white border border-[#DDD8D0] rounded-lg overflow-hidden"
                 data-testid="cat-table">
                <table className="w-full text-[13px]">
                    <thead>
                        <tr className="bg-[#F5F3EE] text-left text-[10px] uppercase tracking-widest text-charcoal/60">
                            <th className="px-3 py-2.5">Code</th>
                            <th className="px-3 py-2.5">Name</th>
                            <th className="px-3 py-2.5">Type</th>
                            <th className="px-3 py-2.5">State</th>
                            <th className="px-3 py-2.5">v</th>
                            <th className="px-3 py-2.5">Lifecycle</th>
                            <th className="px-3 py-2.5">Contract Basis</th>
                            <th className="px-3 py-2.5">Pricing</th>
                            <th className="px-3 py-2.5">WS7</th>
                            <th className="px-3 py-2.5">Projects</th>
                            <th className="px-3 py-2.5"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items === null ? (
                            <tr><td colSpan={11} className="px-3 py-6 text-center text-charcoal/60"
                                    data-testid="cat-loading">Loading catalogue…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={11} className="px-3 py-6 text-center text-charcoal/60"
                                    data-testid="cat-empty">No templates match the current filters.</td></tr>
                        ) : items.map((it) => (
                            <tr key={it.package_code} className="border-t border-[#eee] hover:bg-[#fdfbf8]"
                                data-testid={`cat-row-${it.package_code}`}>
                                <td className="px-3 py-2.5 font-mono text-[12px]">
                                    <Link to={`/admin/service-packages/${it.package_code}/edit`}
                                          className="text-[#A8543F] hover:underline"
                                          data-testid={`cat-open-${it.package_code}`}>
                                        {it.package_code}
                                    </Link>
                                </td>
                                <td className="px-3 py-2.5 font-medium">
                                    <Link to={`/admin/service-packages/${it.package_code}/edit`}
                                          className="hover:underline"
                                          data-testid={`cat-open-name-${it.package_code}`}>
                                        {it.package_name}
                                    </Link>
                                </td>
                                <td className="px-3 py-2.5">{it.template_kind === "Custom Global" ? "Custom" : it.template_kind}</td>
                                <td className="px-3 py-2.5">
                                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11.5px] ${it.catalogue_state === "Active" ? "bg-[#f7efe9] text-[#8f3e2c] border border-[#e3c8bd]" : "bg-[#eee] text-charcoal/60"}`}>
                                        {it.catalogue_state === "Retired" ? "Inactive" : it.catalogue_state}
                                    </span>
                                </td>
                                <td className="px-3 py-2.5">{it.current_version}</td>
                                <td className="px-3 py-2.5">{it.lifecycle_context || "—"}</td>
                                <td className="px-3 py-2.5">{it.contract_basis || "—"}</td>
                                <td className="px-3 py-2.5">{it.pricing_method || "—"}</td>
                                <td className="px-3 py-2.5" data-testid={`cat-ws7-${it.package_code}`}>
                                    {it.ws7_requirement_indicator ? "Yes" : "No"}
                                </td>
                                <td className="px-3 py-2.5" data-testid={`cat-projects-${it.package_code}`}>
                                    {it.projects_using_count}
                                </td>
                                <td className="px-3 py-2.5 text-right">
                                    <button onClick={() => duplicate(it)}
                                            disabled={duplicating === it.package_code}
                                            className="text-[12px] text-[#A8543F] hover:underline disabled:opacity-50"
                                            data-testid={`cat-duplicate-${it.package_code}`}>
                                        Duplicate
                                    </button>
                                </td>
                            </tr>))}
                    </tbody>
                </table>
            </div>
            <p className="text-[11px] text-charcoal/60" data-testid="cat-footnote">
                Package Code and Template Type are immutable.  Duplicate creates a new Custom Global template with a new immutable code.  Existing project package instances are unaffected by catalogue changes.
            </p>
        </div>);
}
